import {
  BasePropertyDTO,
  BooleanPropertyDTO,
  ColorPropertyDTO,
  DBListPropertyDTO,
  DoublePropertyDTO,
  FloatPropertyDTO,
  IntPropertyDTO,
  ListEnumDTO,
  ListPropertyDTO,
  LongPropertyDTO,
  StringPropertyDTO,
} from 'business/models/Models';
import { GlobalPreferencesService } from 'business/services/GlobalPreferencesService';
import RMSelect from 'components/customComponents/Controls/RMSelect';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ReactComponent as ProjectIcon } from '../../../Icons/GlobalPreferences/Project.svg';
import { RMInput } from '../ProteinFormulation/RMInput';
import { CategoryCard } from './CategoryCard';
import RMButton from 'components/customComponents/Controls/RMButton';
import './globalPrefrences.css';
import {
  headerButtonsColor,
  isAdministrator,
  isNullOrUndefined,
  showAlertSnackBar,
} from 'utils/Global';
import { ReactComponent as AdvancedIcon } from '../../../Icons/GlobalPreferences/Advanced.svg';
import { ReactComponent as MiscIcon } from '../../../Icons/GlobalPreferences/Misc.svg';
import { ReactComponent as DropIcon } from '../../../Icons/GlobalPreferences/Drop.svg';
import { ReactComponent as ExperimentIcon } from '../../../Icons/GlobalPreferences/Experiment.svg';
import { ReactComponent as CanvasIcon } from '../../../Icons/GlobalPreferences/Canvas.svg';
import { ReactComponent as DispenseIcon } from '../../../Icons/GlobalPreferences/Dispense.svg';
import { ReactComponent as ETreeIcon } from '../../../Icons/GlobalPreferences/ETree.svg';
import { ReactComponent as ImagingIcon } from '../../../Icons/GlobalPreferences/Imaging.svg';
import { ReactComponent as MatrixIcon } from '../../../Icons/GlobalPreferences/Matrix.svg';
import { ReactComponent as OptimizationIcon } from '../../../Icons/GlobalPreferences/Optimization.svg';
import { ReactComponent as PrintingIcon } from '../../../Icons/GlobalPreferences/Printing.svg';
import { ReactComponent as ScoreIcon } from '../../../Icons/GlobalPreferences/Score.svg';
import cloneDeep from 'lodash/cloneDeep';
import SwitchButton from 'components/customComponents/Controls/SwitchButton';
import '../../../App.css';
import Header from 'components/header/Header';
import { InputWithIcon, SelectInput } from 'components/input';
import RMSvgIcon from 'components/customComponents/CommonComponents/RMSvgIcon';
import { useHistory } from 'react-router-dom';
import { SettingsTabsContext } from '../Settings/SettingsTabs';
import { IStyle } from 'Styles';
import { LoadingModal, RMModal } from 'components/modal';
import {
  isPrintLineProperty,
  isStandardPrintLineProperty,
  MAX_STANDARD_PRINT_LINE_TAGS,
  PREFERENCE_SECTION_GROUPS,
  Preference,
  printLineParametersItems,
} from './constants';
import PlateTypeService from 'business/services/PlateTypeService';
import { IOption } from 'components/input/selectInput';
import { FmlxButton, FmlxModal } from 'fmlx-common-ui';
import { useSavePreferences } from './hooks';
import { KeyboardKeys, VerticalArrowKeys } from 'constants/';
import { useContainerFocus } from 'CustomHooks/useContainerFocus';
import EAlertType from 'enums/EAlertType';
import ESnackBarPosition from 'enums/ESnackBarPosition';
import { validateProperty, selectedPrintLineParametersItems } from './utils';
import { useSaveDiscardButtons } from 'CustomHooks/useSaveDiscardButtons';
import { PrintLinePropertyInput } from './PrintLinePropertyInput';

type CategoryProperty = {
  categoryName: string;
  props: BasePropertyDTO[];
  selected?: boolean;
  icon?: any;
};
let beforeSearchSelectedCategory = -1;
let updatedProperties: BasePropertyDTO[] = [];
let unchangedProperties: CategoryProperty[] = [];
let searchedProperties: CategoryProperty[] = [];

export const GlobalPreferences = () => {
  const preferenceType = window?.location?.pathname.includes('/globalpreferences')
    ? 'global'
    : 'user';
  const [categories, setCategories] = useState<CategoryProperty[]>([]);
  const [propsView, setPropsView] = useState<any[]>([]);
  const [searchText, setSearchText] = useState<string>('');
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const { savePreferences } = useSavePreferences();
  const [saveDisabled, setSaveDisabled] = useState<boolean>(true);
  const [loadingText, setLoadingText] = useState<string>('Loading');
  const history = useHistory();
  const { setHeaders } = React.useContext(SettingsTabsContext) || {};
  const [originalPlateTypes, setOriginalPlateTypes] = useState([]);
  const [selectedPlateId, setSelectedPlateId] = useState<number>(0);
  const [showContainers, setShowContainers] = useState<boolean>(false);
  const containerRef = useRef(null);
  const parentContainerRef = useRef();
  const [isPlatePropertiesSearched, setIsPlatePropertiesSearched] =
    useState<boolean>(false);
  const [isFocused] = useContainerFocus(parentContainerRef, true);
  const PLATE_CATEGORY_NAME = 'Plate';

  const handlePlateTypeSelect = (e: any) => {
    setSelectedPlateId(e.target.value);
  };

  useEffect(() => {
    updatePropertiesView();
  }, [selectedPlateId, isPlatePropertiesSearched, searchText, categories]);

  const containers = useMemo(() => {
    if (originalPlateTypes == null) {
      return [];
    }
    const arr: IOption[] = originalPlateTypes?.map((robot) => {
      return {
        value: robot.id,
        title: robot.name,
      };
    });
    return arr;
  }, [originalPlateTypes, selectedPlateId]);

  useEffect(() => {
    getPropNames();
    setIsLoading(true);

    async function getPropNames() {
      const gpService: GlobalPreferencesService = new GlobalPreferencesService();
      const plateTypeService: PlateTypeService = new PlateTypeService();

      let allProps: BasePropertyDTO[];
      if (preferenceType == 'global') {
        allProps = await gpService.getAllPropNames(Preference.GlobalPreference);
      } else if (preferenceType == 'user') {
        allProps = await gpService.getAllPropNames(Preference.UserPreference);
      }

      allProps.forEach((prop) => {
        if (
          prop.categoryName != '' &&
          categories.findIndex((cat) => cat.categoryName == prop.categoryName) < 0
        ) {
          categories.push({ categoryName: prop.categoryName, props: [prop] });
        } else {
          categories.find((c) => c.categoryName == prop.categoryName).props.push(prop);
        }
      });
      const displayCategories = categoriesDisplayNameConverter(categories);
      updatePropertiesView();
      setCategories([...displayCategories]);
      unchangedProperties = cloneDeep([...displayCategories]);
      setIsLoading(false);

      const plateTypes = await plateTypeService.getAllPlateTypes();
      setSelectedPlateId(plateTypes.saveContainerDTOs[0].id);
      setOriginalPlateTypes(plateTypes.saveContainerDTOs);
    }
  }, []);
  useEffect(() => {
    if (setHeaders) {
      setHeaders(
        <Header
          title={preferenceType == 'global' ? 'Global Preferences' : 'User Preferences'}
          isNewTheme={true}
          headerButtons={[
            <InputWithIcon
              key={`ab${searchText}`}
              icon={<RMSvgIcon name="Search" fill={headerButtonsColor} />}
              onChange={searchChangeHandler}
              value={searchText}
              containerStyle={{ marginRight: '20px', width: '396px' }}
              inputStyle={{ height: '2.5rem' }}
            />,
          ]}
          extendedActionSection={saveDiscardButtons}
        />,
      );
    }
  }, [searchText, saveDisabled, categories, unchangedProperties, searchedProperties]);

  function onChangeProp(prop: BasePropertyDTO, e: any) {
    if (prop instanceof ListEnumDTO) {
      const val = prop.options.find((opt) => opt.id === parseInt(e.target.value));
      prop.value = val;
    } else if (prop instanceof DBListPropertyDTO || prop instanceof ListPropertyDTO) {
      prop.value = prop.options.find((opt) => opt.name === e.target.value);
    } else if (prop instanceof ColorPropertyDTO) {
      prop.value = e;
    } else if (prop instanceof IntPropertyDTO || prop instanceof LongPropertyDTO) {
      prop.value = parseInt(e.target.value);
    } else if (prop instanceof DoublePropertyDTO || prop instanceof FloatPropertyDTO) {
      prop.value = e.target.value;
    } else if (prop instanceof StringPropertyDTO) {
      prop.value = e.target.value;
    } else if (prop instanceof BooleanPropertyDTO) {
      prop.value = e;
    }

    if (prop.key) {
      const idx = updatedProperties.findIndex(
        (p) => p.name === prop.name && p.key === prop.key,
      );
      if (idx >= 0) {
        updatedProperties[idx] = prop;
      } else {
        updatedProperties.push(prop);
      }
    } else {
      const idx = updatedProperties.findIndex((p) => p.name === prop.name);
      if (idx >= 0) {
        updatedProperties[idx] = prop;
      } else {
        updatedProperties.push(prop);
      }
    }

    setSaveDisabled(false);
    setCategories([...categories]);
    updatePropertiesView();
  }

  function buildPropsViewWithSections(
    categoryName: string,
    props: BasePropertyDTO[],
  ): React.ReactNode[] {
    const sectionGroups = PREFERENCE_SECTION_GROUPS[categoryName];
    if (!sectionGroups?.length) {
      return renderProperties(props);
    }
    const allSectionNames = new Set(sectionGroups.flatMap((g) => [...g.propertyNames]));
    const ungrouped = props.filter((p) => !allSectionNames.has(p.name ?? ''));
    const categorySlug = categoryName.toLowerCase().replace(/\s+/g, '-');
    const result: React.ReactNode[] = [...renderProperties(ungrouped)];
    sectionGroups.forEach((sec) => {
      const groupProps = (sec.propertyNames as string[])
        .map((name) => props.find((p) => p.name === name))
        .filter(Boolean) as BasePropertyDTO[];
      if (groupProps.length > 0) {
        const sectionSlug = sec.title.toLowerCase().replace(/\s+/g, '-');
        const sectionId = `globalpreferences-section-${categorySlug}-${sectionSlug}`;
        result.push(
          <div key={sectionId} id={sectionId} className="text-h3 text-black mb-1">
            {sec.title}
          </div>,
          ...(sec.description != ''
            ? [
                <div
                  key={`${sectionId}-description`}
                  id={`${sectionId}-description`}
                  className="text-sm text-gray-600 mb-3"
                >
                  {sec.description}
                </div>,
              ]
            : []),
          ...renderProperties(groupProps),
        );
      }
    });
    return result;
  }

  function updatePropertiesView() {
    let propsView;
    if (searchedProperties.length <= 0) {
      const selectedCategory = categories.find((c) => c.selected === true);
      if (!isNullOrUndefined(selectedCategory)) {
        if (selectedCategory.categoryName === PLATE_CATEGORY_NAME) {
          if (isPlatePropertiesSearched) {
            setPropsView(propsView);
            return;
          }
          const props = selectedCategory.props.filter(
            (p) => p.key.foreignID == selectedPlateId,
          );
          propsView = renderProperties(props);
          setShowContainers(true);
        } else {
          setShowContainers(false);
          propsView = buildPropsViewWithSections(
            selectedCategory.categoryName,
            selectedCategory.props,
          );
        }
      }
    } else {
      propsView = [];

      const selectedIndex = categories.findIndex((c) => c.selected === true);
      if (categories[selectedIndex]?.categoryName === PLATE_CATEGORY_NAME) {
        updateSearchPropertiesAccordingToContainer(selectedIndex, searchText);
      }

      searchedProperties.forEach((cat) => {
        const catName = (
          <div
            id={`globalpreferences-updatepropertiesview-${cat.categoryName
              .toLowerCase()
              .replace(/\s+/g, '-')}`}
            className="text-h3 text-black"
          >
            {' '}
            {cat.categoryName}{' '}
          </div>
        );
        propsView = [
          ...propsView,
          catName,
          ...buildPropsViewWithSections(cat.categoryName, cat.props),
        ];
      });
    }
    setPropsView(propsView);
  }

  const updateSearchPropertiesAccordingToContainer = (
    selectedIndex: number,
    searchTerm = '',
  ) => {
    const filteredProperties = categories[selectedIndex].props.filter(
      (p) => p.key.foreignID == selectedPlateId,
    );
    searchedProperties = [];
    for (let i = 0; i < filteredProperties.length; i++) {
      if (
        filteredProperties[i].displayName
          .toLocaleLowerCase()
          .indexOf(searchTerm.toLowerCase()) >= 0
      ) {
        const index = searchedProperties.findIndex(
          (cat) => cat.categoryName == categories[selectedIndex].categoryName,
        );
        if (index < 0) {
          searchedProperties.push({
            categoryName: categories[selectedIndex].categoryName,
            props: [filteredProperties[i]],
          });
        } else {
          searchedProperties
            .find((c) => c.categoryName == categories[selectedIndex].categoryName)
            .props.push(filteredProperties[i]);
        }
      }
    }
  };

  const hasPrintLineErrors = useMemo(() => {
    const selectedCategory = categories.find((c) => c.selected === true);
    if (!selectedCategory) {
      return false;
    }

    for (const prop of selectedCategory.props) {
      if (isPrintLineProperty(prop.name) && isStandardPrintLineProperty(prop.name)) {
        const selectedItems = selectedPrintLineParametersItems(
          prop as StringPropertyDTO,
          printLineParametersItems,
        );
        if (selectedItems.length > MAX_STANDARD_PRINT_LINE_TAGS) {
          return true;
        }
      }
    }

    if (searchedProperties.length > 0) {
      for (const cat of searchedProperties) {
        for (const prop of cat.props) {
          if (isPrintLineProperty(prop.name) && isStandardPrintLineProperty(prop.name)) {
            const selectedItems = selectedPrintLineParametersItems(
              prop as StringPropertyDTO,
              printLineParametersItems,
            );
            if (selectedItems.length > MAX_STANDARD_PRINT_LINE_TAGS) {
              return true;
            }
          }
        }
      }
    }

    return false;
  }, [categories, searchedProperties]);

  function renderProperties(props) {
    const tempPropsView = [];
    let inputTag;
    props.forEach((prop) => {
      if (isPrintLineProperty(prop.name)) {
        inputTag = <PrintLinePropertyInput prop={prop} onChangeProp={onChangeProp} />;
      } else if (prop instanceof DBListPropertyDTO || prop instanceof ListPropertyDTO) {
        inputTag = (
          <RMSelect
            key={prop.name}
            className="flex-50"
            style={{ heigth: '2rem' }}
            options={prop.options.map((opt) => {
              return { id: parseInt(opt.name), name: opt.displayName };
            })}
            selectedValue={parseInt(prop.value.name)}
            onChange={(e) => onChangeProp(prop, e)}
            placeholder={'any'}
            disabled={false}
          />
        );
      } else if (prop instanceof ListEnumDTO) {
        inputTag = (
          <RMSelect
            key={prop.name}
            className="flex-50"
            style={{ heigth: '2rem' }}
            options={prop.options.map((opt) => {
              return { id: opt.id, name: opt.displayName };
            })}
            selectedValue={prop.value.id}
            onChange={(e) => onChangeProp(prop, e)}
            placeholder={'any'}
            disabled={false}
          />
        );
      } else if (prop instanceof StringPropertyDTO) {
        inputTag = (
          <input
            id={`globalpreferences-renderproperties-input-string-${prop.name
              .toLowerCase()
              .replace(/\s+/g, '-')}`}
            key={prop.name}
            className="inputFieldDetails flex-50"
            type="text"
            onChange={(e) => {
              onChangeProp(prop, e);
            }}
            value={prop.value}
          />
        );
      } else if (
        prop instanceof IntPropertyDTO ||
        prop instanceof DoublePropertyDTO ||
        prop instanceof LongPropertyDTO ||
        prop instanceof FloatPropertyDTO
      ) {
        inputTag = (
          <RMInput
            key={prop.name}
            className="inputFieldDetails flex-50"
            onChange={(e) => {
              onChangeProp(prop, e);
            }}
            value={prop.value}
            allowDecimals={
              prop instanceof DoublePropertyDTO || prop instanceof FloatPropertyDTO
            }
          />
        );
      } else if (prop instanceof BooleanPropertyDTO) {
        inputTag = (
          <div
            id={`globalpreferences-renderproperties-input-boolean-wrapper-${prop.name
              .toLowerCase()
              .replace(/\s+/g, '-')}`}
            className="flex-50"
          >
            <SwitchButton
              key={prop.name}
              onToggle={(e) => {
                onChangeProp(prop, e);
              }}
              isOn={prop.value}
            />
          </div>
        );
      } else if (prop instanceof ColorPropertyDTO) {
        inputTag = (
          <RMColorPicker
            className="flex-50 "
            key={prop.name}
            color={prop.value}
            onChange={(e) => onChangeProp(prop, e)}
            showChangeButton={true}
            colorWidth="17"
            colorHeight="17"
            buttonStyle={styles.buttonStyle}
            containerStyle={styles.containerStyle}
          />
        );
      }
      tempPropsView.push(
        <div
          id={`globalpreferences-renderProperties-temppropsview-${prop.name
            .toLowerCase()
            .replace(/\s+/g, '-')}`}
          key={prop.name + '_div'}
          style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'baseline' }}
        >
          <label key={prop.name + '_label'} className="text-label flex-50">
            {prop.displayName}
          </label>
          {inputTag}
        </div>,
      );
    });
    return tempPropsView;
  }

  function categoriesDisplayNameConverter(categories: CategoryProperty[]) {
    for (let i = 0; i < categories.length; i++) {
      switch (categories[i].categoryName.toLowerCase()) {
        case 'project':
          categories[i].categoryName = 'Project';
          categories[i].selected = true;
          beforeSearchSelectedCategory = i;
          categories[i].icon = <ProjectIcon />;
          break;
        case 'experiment':
          categories[i].categoryName = 'Experiment';
          categories[i].icon = <ExperimentIcon />;
          break;
        case 'canvas':
          categories[i].categoryName = 'Canvas';
          categories[i].icon = <CanvasIcon />;
          break;
        case 'drop':
          categories[i].categoryName = 'Drop';
          categories[i].icon = <DropIcon />;
          break;
        case 'scoring':
          categories[i].categoryName = 'Scoring';
          categories[i].icon = <ScoreIcon />;
          break;
        case 'explorertree':
          categories[i].categoryName = 'Explorer Tree';
          categories[i].icon = <ETreeIcon />;
          break;
        case 'labelprinting':
          categories[i].categoryName = 'Label Printing';
          categories[i].icon = <PrintingIcon />;
          break;
        case 'matrixmaker':
          categories[i].categoryName = 'Matrix Maker';
          categories[i].icon = <MatrixIcon />;
          break;
        case 'imaging':
          categories[i].categoryName = 'Imaging';
          categories[i].icon = <ImagingIcon />;
          break;
        case 'optimization':
          categories[i].categoryName = 'Optimization';
          categories[i].icon = <OptimizationIcon />;
          break;
        case 'dispensingrobots':
          categories[i].categoryName = 'Dispensing Robots';
          categories[i].icon = <DispenseIcon />;
          break;
        case 'misc':
          categories[i].categoryName = 'Miscellaneous';
          categories[i].icon = <MiscIcon />;
          break;
        case 'advanced':
          categories[i].categoryName = 'Advanced';
          categories[i].icon = <AdvancedIcon />;
          break;
        case 'plate':
          categories[i].categoryName = PLATE_CATEGORY_NAME;
          categories[i].icon = <RMSvgIcon name="Plate" fill="#ffffff" />;
          break;
        default:
          break;
      }
    }
    return categories;
  }

  function setSelectedCategory(categoryName: string) {
    categories.map((c) => (c.selected = false));
    categories.find((c) => c.categoryName == categoryName).selected = true;
    if (containerRef.current) {
      containerRef.current.scrollTop = 0;
    }
    searchedProperties = [];
    setSearchText('');
    updatePropertiesView();
    setCategories([...categories]);
    setIsPlatePropertiesSearched(false);
  }

  function searchChangeHandler(e) {
    setSearchText(e.target.value);
    const selectedIndex = categories.findIndex((c) => c.selected === true);
    if (selectedIndex >= 0) {
      beforeSearchSelectedCategory = selectedIndex;
      if (categories[selectedIndex].categoryName !== PLATE_CATEGORY_NAME) {
        categories[selectedIndex].selected = false;
      } else {
        setIsPlatePropertiesSearched(true);
      }
    }
    searchedProperties = [];
    if (e.target.value === '') {
      setSelectedCategory(categories[beforeSearchSelectedCategory].categoryName);
      setIsPlatePropertiesSearched(false);
    } else {
      const keyword = e.target.value;

      if (categories[selectedIndex]?.categoryName === PLATE_CATEGORY_NAME) {
        updateSearchPropertiesAccordingToContainer(selectedIndex, keyword);
        updatePropertiesView();
        return;
      }

      for (let i = 0; i < categories.length; i++) {
        for (let j = 0; j < categories[i].props.length; j++) {
          if (categories[i].categoryName !== PLATE_CATEGORY_NAME) {
            if (
              categories[i].props[j].displayName
                .toLocaleLowerCase()
                .indexOf(keyword.toLowerCase()) >= 0
            ) {
              const index = searchedProperties.findIndex(
                (cat) => cat.categoryName == categories[i].categoryName,
              );
              if (index < 0) {
                searchedProperties.push({
                  categoryName: categories[i].categoryName,
                  props: [categories[i].props[j]],
                });
              } else {
                searchedProperties
                  .find((c) => c.categoryName == categories[i].categoryName)
                  .props.push(categories[i].props[j]);
              }
            }
          }
        }
      }
      updatePropertiesView();
    }
  }

  async function saveHandlder() {
    if (hasPrintLineErrors) {
      showAlertSnackBar(
        EAlertType.ERROR,
        ESnackBarPosition.TOP,
        `Print Label cannot exceed ${MAX_STANDARD_PRINT_LINE_TAGS} tags. Please fix the error before saving.`,
      );
      return;
    }

    const invalidProps: string[] = [];
    const allProperties = categories.flatMap((c) => c.props);

    for (const prop of updatedProperties) {
      const error = validateProperty(prop, allProperties);
      if (error) {
        invalidProps.push(`${prop.displayName || prop.name}: ${error}`);
      }
    }

    if (invalidProps.length > 0) {
      showAlertSnackBar(
        EAlertType.ERROR,
        ESnackBarPosition.TOP,
        `Validation failed:\n${invalidProps[0]}`,
      );
      return;
    }

    setLoadingText('Saving');
    setIsLoading(true);
    await savePreferences({
      updatedProperties,
      preferenceType,
      onSuccess: () => {
        setSaveDisabled(true);
        updatedProperties = [];
        unchangedProperties = cloneDeep([...categories]);
      },
      onFinally: () => {
        setIsLoading(false);
      },
    });
  }

  function discardHandler() {
    const selectedCategoryName = categories.find(
      (c) => c.selected === true,
    )?.categoryName;
    const tempCategories = cloneDeep([...unchangedProperties]);
    tempCategories.map((c) => {
      c.selected = c.categoryName === selectedCategoryName;
    });

    setCategories([...tempCategories]);
    updatedProperties = [];
    setSaveDisabled(true);
    updatePropertiesView();
  }
  const saveDiscardButtons = useSaveDiscardButtons({
    enableButtons: !saveDisabled,
    onSave: saveHandlder,
    onDiscard: discardHandler,
  });

  if (!isAdministrator()) {
    history.push('/project');
  }

  const changeSelectedCategory = (direction: 'next' | 'previous') => {
    const currentCategoryIndex = categories.findIndex((cat) => cat.selected);
    const newIndex =
      direction === 'next'
        ? (currentCategoryIndex + 1) % categories.length
        : (currentCategoryIndex - 1 + categories.length) % categories.length;

    const item = document.getElementById(
      `globalpreference-${categories[newIndex].categoryName}`,
    );
    setSelectedCategory(categories[newIndex].categoryName);
    if (item) {
      item.scrollIntoView({
        block: 'nearest',
      });
    }
  };

  const handleUserKeyPress = useCallback(
    (e: KeyboardEvent) => {
      if (!isFocused) {
        return;
      }
      const ctrlKey = e.ctrlKey || e.metaKey;

      if (ctrlKey && e.key === KeyboardKeys.S) {
        e.preventDefault();
        if (!saveDisabled) {
          saveHandlder();
        }
      } else if (VerticalArrowKeys.includes(e.key as KeyboardKeys)) {
        e.preventDefault();
        changeSelectedCategory(e.key === KeyboardKeys.ArrowDown ? 'next' : 'previous');
      }
    },
    [saveDisabled, saveHandlder],
  );

  useEffect(() => {
    const abortController = new AbortController();
    const signal = abortController.signal;

    window.addEventListener('keydown', handleUserKeyPress, { signal });

    return () => {
      abortController.abort();
    };
  }, [handleUserKeyPress]);

  return (
    <div id="globalpreferences-wrapper" ref={parentContainerRef} className="h-[100%]">
      <div
        id="globalpreferences-layout"
        style={{ display: 'flex' }}
        className="h-[100%] flex overflow-hidden"
      >
        <div
          id="globalpreferences-categories"
          style={{
            overflowY: 'auto',
            paddingBottom: 20,
          }}
          className="m-2 w-[16%] space-y-2 flex flex-col overflow-y-auto"
        >
          {categories.map((cat) => {
            return (
              <CategoryCard
                id={`globalpreference-${cat.categoryName}`}
                key={cat.categoryName}
                icon={cat.icon}
                text={cat.categoryName}
                onClick={setSelectedCategory}
                selected={cat.selected}
                iconName={''}
                iconSubFolder={''}
              />
            );
          })}
        </div>
        {showContainers ? (
          <div
            id="globalpreferences-plate-panel"
            style={{
              flex: '84%',
              border: '1px dashed #9E9E9E',
              overflowY: 'auto',
              gap: '0.5rem',
              flexDirection: 'column',
              padding: '2% 2% 2% 2%',
            }}
            className="flex m-2 h-[97%] overflow-auto flex-col"
          >
            <div
              id="globalpreferences-plate-header"
              className="flex flex-row justify-between"
            >
              <div id="globalpreferences-plate-title" className="flex flex-grow-[1]">
                Plate Type Name
              </div>
              <div id="globalpreferences-plate-select" className="flex flex-grow-[2]">
                <SelectInput
                  className="w-full"
                  options={containers}
                  value={selectedPlateId}
                  onChange={handlePlateTypeSelect}
                />
              </div>
            </div>
            <div
              id="globalpreferences-plate-content"
              style={{
                flex: '84%',
                border: '1px dashed #9E9E9E',
                overflowY: 'auto',
                gap: '0.5rem',
                flexDirection: 'column',
                padding: '2% 25% 2% 2%',
              }}
              className="flex m-2 flex-1"
            >
              {propsView}
            </div>
          </div>
        ) : (
          <div
            id="globalpreferences-plate-props"
            ref={containerRef}
            style={{
              flex: '84%',
              border: '1px dashed #9E9E9E',
              overflowY: 'auto',
              gap: '0.5rem',
              flexDirection: 'column',
              padding: '2% 25% 2% 2%',
            }}
            className="flex m-2 flex-1"
          >
            {propsView}
          </div>
        )}
      </div>
      <LoadingModal showModal={isLoading} text={loadingText} />
    </div>
  );
};

export const RMColorPicker = ({
  color,
  onChange,
  className = '',
  showChangeButton = null,
  index = 0,
  colorHeight = '32',
  colorWidth = '32',
  buttonStyle = {},
  containerStyle = {},
}) => {
  const [tempColor, setTempColor] = useState(color);
  const [show, setShow] = useState(false);

  const colorsMap = [
    '#FF0000',
    '#008000',
    '#0000FF',
    '#FFA500',
    '#FF00FF',
    '#00FFFF',
    '#FFFF00',
    '#00FF00',
    '#A52A2A',
    '#FF69B4',
    '#FFFF00',
    '#CD853F',
    '#4169E1',
    '#800080',
    '#4B0082',
    '#808000',
    '#DA70D6',
    '#98FB98',
    '#DDA0DD',
    '#FA8072',
    '#008080',
    '#FF6347',
    '#B22222',
    '#6495ED',
    '#DC143C',
    '#FFFFFF',
    '#CCCCCC',
    '#999999',
    '#666666',
    '#333333',
    '#000000',
  ];

  useEffect(() => {
    if (color !== tempColor) {
      setTempColor(color);
    }
  }, [color]);

  let rmButton = <></>;
  if (showChangeButton) {
    rmButton = (
      <RMButton
        id={`globalpreferences-color-change-btn-${index}`}
        customStyle={{
          width: '6.0rem',
          height: '2rem',
          maxHeight: '2rem',
          minHeight: '2rem',
          borderRadius: '0.5rem',
          fontSize: '1.25rem',
          lineHeight: '1.45rem',
          ...buttonStyle,
        }}
        onClick={(e) => setShow(true)}
        label="Change"
        type={FmlxButton.Type.POSITIVE}
        size={FmlxButton.Size.SMALL}
      ></RMButton>
    );
  }
  return (
    <div
      id={`globalpreferences-colorpicker-${index}`}
      className={'flex ' + className}
      style={{ gap: '0.5rem', ...containerStyle }}
    >
      <svg
        id={`globalpreferences-color-svg-${index}`}
        width={(Number(colorWidth) + 1).toString()}
        height={(Number(colorHeight) + 1).toString()}
        viewBox={`0 0 ${(Number(colorWidth) + 1).toString()} ${(
          Number(colorHeight) + 1
        ).toString()}`}
        fill={'none'}
        xmlns="http://www.w3.org/2000/svg"
        onClick={(e) => setShow(true)}
      >
        <rect
          id={`globalpreferences-color-rect-${index}`}
          x="0.5"
          y="0.5"
          width={colorWidth}
          height={colorHeight}
          rx="3.5"
          fill={tempColor}
          stroke="#858585"
        />
      </svg>
      {rmButton}
      <RMModal
        id={`globalpreferences-color-modal-${index}`}
        size={FmlxModal.Size.SMALL}
        showButtons={false}
        heading="Color Picker"
        showModal={show}
        onHide={() => setShow(false)}
        setShowModal={setShow}
        body={
          <div
            id={`globalpreferences-color-modal-body-${index}`}
            style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}
          >
            <div
              id={`globalpreferences-color-palette-${index}`}
              style={{ justifyContent: 'center' }}
            >
              {colorsMap.map((item, index) => (
                <RMSvgIcon
                  key={`colorss${index}`}
                  id={`globalpreferences-color-swatch-${index}`}
                  style={{
                    marginLeft: '10px',
                    marginTop: '5px',
                    marginBottom: '5px',
                    border: '1px solid black',
                  }}
                  name={'ScoresColor'}
                  fill={item}
                  onClick={() => setTempColor(item)}
                />
              ))}
            </div>
            <div
              id={`globalpreferences-color-modal-actions-${index}`}
              style={{
                display: 'flex',
                flexDirection: 'row',
                gap: '0.5rem',
                justifyContent: 'center',
              }}
            >
              <RMButton
                id={`globalpreferences-color-ok-${index}`}
                customStyle={{
                  width: '6rem',
                  height: '2rem',
                  maxHeight: '2rem',
                  minHeight: '2rem',
                  borderRadius: '0.5rem',
                  fontSize: '1.25rem',
                  lineHeight: '1.45rem',
                }}
                onClick={(e) => {
                  onChange(tempColor, index);
                  setShow(false);
                }}
                label="OK"
              ></RMButton>
              <RMButton
                id={`globalpreferences-color-cancel-${index}`}
                customStyle={{
                  width: '6rem',
                  height: '2rem',
                  maxHeight: '2rem',
                  minHeight: '2rem',
                  borderRadius: '0.5rem',
                  fontSize: '1.25rem',
                  lineHeight: '1.45rem',
                }}
                onClick={(e) => {
                  setTempColor(color);
                  setShow(false);
                }}
                label="Cancel"
              ></RMButton>
            </div>
          </div>
        }
      />
    </div>
  );
};

const styles: IStyle = {
  buttonStyle: {
    minHeight: '17px',
    maxHeight: '17px',
    fontSize: '1.0rem',
  },
  containerStyle: {
    alignItems: 'center',
    paddingTop: '2px',
  },
};
